import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { DealThesisSidebarTabs } from '@app/shared/models/deal-thesis-sidebar-context-enum';
import { OverallExpandState } from '@app/shared/components/expand-all/ExpandAllOpm';
import { AppDispatch, RootState } from './store';
import {
  ApplicationConfigurationDto,
  CompatibilityLevelDto,
  MaintenanceModeDto,
} from '@app/shared/models/contracts/application-configuration-dto';
import api from '@server/api-config';
import env from '@environment';
import { setServerFeatureFlags } from '@core/feature-flags/feature-flags';
import { LOADER_HIDE_TIMEOUT } from '@app/shared/constants/duration';
import {
  OperationProgressUpdateDto,
  OperationsStepType,
} from '@app/shared/models/contracts/operation-progress-update-dto';
import { ChildRowState } from '@app/modules/projects/dashboard/pwerm2-widgets/valuation-conclusion/ValuationConclusions';
import { ChartZoomMode } from '@app/modules/projects/dashboard/opm-widgets/stacked-distribution/opm-enums';
import { WaterfallSectionsErf } from '@app/shared/models/contracts/enums/shared-enums';
import { ExpandedSectionsState } from '@app/modules/projects/outputs/opm-summary-waterfall/sample-waterfall/sample-waterfall-outputs/SampleWaterfallOutputsSummary';
import { FirstLastExpandedSectionsState } from '@app/modules/projects/outputs/opm-summary-waterfall/first-last-5-erf/instrument-calculations-erf/InstrumentCalculationsErf';

type NestedBooleanObject = {
  [key: string]: boolean | NestedBooleanObject;
};

type PageScrollPosition = {
  pageName: string;
  scrollTop: number;
  scrollLeft: number;
};

type UserSelections = {
  opmWaterfall: {
    selectedTab: Optional<string>;
    firstLastTable: {
      selectedYear: Optional<string>;
      selectedInstrument: Optional<string>;
    };
    sampleWaterfall: {
      selectedYear: Optional<string>;
      selectedInstrument: Optional<string>;
      selectedEnterpriseValue: Nullable<number>;
    };
  };
  dealThesis: {
    selectedTab: Optional<string>;
    showHistoricDataColumns: Optional<boolean>;
  };
  opmInputs: {
    showHistoricDataColumns: boolean;
  };
  pwermDashboard: {
    valuationEstimatesTable: {
      selectedInstrument: Optional<string>;
    };
    evEvolutionChart: {
      selectedCase: Optional<string>;
      isExpanded: Optional<boolean>;
    };
    totalEquityChart: {
      selectedCase: Optional<string>;
      isExpanded: Optional<boolean>;
    };
    keyMetricsAndCrossChecksTable: {
      isExpanded: Optional<boolean>;
    };
  };
  pwerm2Dashboard: {
    valuationEstimatesTable: {
      selectedInstrument: Optional<string>;
    };
    evEvolutionChart: {
      selectedCase: Optional<string>;
      isExpanded: Optional<boolean>;
    };
    totalEquityChart: {
      selectedCase: Optional<string>;
      isExpanded: Optional<boolean>;
    };
    keyMetricsAndCrossChecksTable: {
      isExpanded: Optional<boolean>;
    };
  };
  opmDashboard: {
    riskNeutralChart: {
      selectedZoom: {
        selectedZoomMode: Optional<ChartZoomMode>;
        selectedZoomMaximum: Optional<number>;
        selectedZoomMinimum: Optional<number>;
      };
      selectedYear: Optional<string>;
      selectedInstrument: Optional<string>;
      isExpanded: Optional<boolean>;
    };
    valuationEstimatesTable: {
      childRowStates: ChildRowState;
    };
    inputSummaryTable: {
      isEventsExpanded: Optional<boolean>;
      isDlocInstrumentsExpanded: Optional<boolean>;
      isDlomInstrumentsExpanded: Optional<boolean>;
    };
  };
  realWorldDistributions: {
    realWorldChart: {
      selectedZoom: {
        selectedZoomMode: Optional<ChartZoomMode>;
        selectedZoomMaximum: Optional<number>;
        selectedZoomMinimum: Optional<number>;
      };
      selectedYear: Optional<string>;
      selectedInstrument: Optional<string>;
    };
    realWorldEvChart: {
      selectedZoom: {
        selectedZoomMode: Optional<ChartZoomMode>;
        selectedZoomMaximum: Optional<number>;
        selectedZoomMinimum: Optional<number>;
      };
      selectedYear: Optional<string>;
    };
    realWorldTotalAttributableEquityChart: {
      selectedZoom: {
        selectedZoomMode: Optional<ChartZoomMode>;
        selectedZoomMaximum: Optional<number>;
        selectedZoomMinimum: Optional<number>;
      };
      selectedYear: Optional<string>;
    };
    realWorldByInstrumentChart: {
      selectedZoom: {
        selectedZoomMode: Optional<ChartZoomMode>;
        selectedZoomMaximum: Optional<number>;
        selectedZoomMinimum: Optional<number>;
      };
      selectedYear: Optional<string>;
      selectedInstrument: Optional<string>;
    };
  };
  costOfEquity: {
    selectedTab: Optional<string>;
  };
  capitalStructure: {
    selectedTab: Optional<string>;
    buildStructureOwnerTabs: {
      [key: string]: number;
    };
    buildStructureOwnerTabsErf: {
      [key: string]: number[];
    };
    buildStructure: {
      selectedCase: Optional<string>;
    };
    buildStructureExpandedEvents: {
      [eventId: string]: boolean;
    };
    expandStructureInstruments: {
      [eventId: string]: { [instrumentKey: string]: boolean };
    };
    expandStructureSelectedRadio: {
      [eventId: string]: string;
    };
    capTableErfSelectedEventIndex: Optional<number>;
    payoutLogic: {
      selectedSubTab: Optional<string>;
      tabsHorizontalScrollPosition: Nullable<number>;
    };
    dividends: {
      showHistoricYears: boolean;
      selectedCase: Optional<string>;
      lastPwermCase: Optional<string>;
    };
    participationTable: {
      selectedYear: Optional<string>;
    };
  };
  valuationInputs: {
    selectedTab: Optional<string>;
  };
  overallConclusions: {
    showPerShareFigures: Optional<boolean>;
  };
  sampleWaterfallTableExpandStates: ExpandedSectionsState;
  firstLastWaterfallTableExpandStates: FirstLastExpandedSectionsState;
  sponsorIrrAndHurdle: {
    selectedTab: Optional<string>;
    irrHurdleCalculations: {
      selectedCase: Optional<string>;
      selectedInstrument: Optional<string>;
    };
    sponsorIrr: {
      selectedCase: Optional<string>;
    };
  };
};

const initialUserSelections: UserSelections = {
  opmWaterfall: {
    selectedTab: undefined,
    firstLastTable: {
      selectedYear: undefined,
      selectedInstrument: undefined,
    },
    sampleWaterfall: {
      selectedYear: undefined,
      selectedInstrument: undefined,
      selectedEnterpriseValue: null,
    },
  },
  dealThesis: {
    selectedTab: undefined,
    showHistoricDataColumns: true,
  },
  opmInputs: {
    showHistoricDataColumns: false,
  },
  pwermDashboard: {
    valuationEstimatesTable: {
      selectedInstrument: undefined,
    },
    evEvolutionChart: {
      selectedCase: undefined,
      isExpanded: undefined,
    },
    totalEquityChart: {
      selectedCase: undefined,
      isExpanded: undefined,
    },
    keyMetricsAndCrossChecksTable: {
      isExpanded: undefined,
    },
  },
  pwerm2Dashboard: {
    valuationEstimatesTable: {
      selectedInstrument: undefined,
    },
    evEvolutionChart: {
      selectedCase: undefined,
      isExpanded: undefined,
    },
    totalEquityChart: {
      selectedCase: undefined,
      isExpanded: undefined,
    },
    keyMetricsAndCrossChecksTable: {
      isExpanded: undefined,
    },
  },
  opmDashboard: {
    riskNeutralChart: {
      selectedYear: undefined,
      selectedInstrument: undefined,
      isExpanded: undefined,
      selectedZoom: {
        selectedZoomMode: undefined,
        selectedZoomMinimum: undefined,
        selectedZoomMaximum: undefined,
      },
    },
    valuationEstimatesTable: {
      childRowStates: {},
    },
    inputSummaryTable: {
      isEventsExpanded: undefined,
      isDlocInstrumentsExpanded: undefined,
      isDlomInstrumentsExpanded: undefined,
    },
  },
  realWorldDistributions: {
    realWorldChart: {
      selectedYear: undefined,
      selectedInstrument: undefined,
      selectedZoom: {
        selectedZoomMode: undefined,
        selectedZoomMinimum: undefined,
        selectedZoomMaximum: undefined,
      },
    },
    realWorldEvChart: {
      selectedYear: undefined,
      selectedZoom: {
        selectedZoomMode: undefined,
        selectedZoomMinimum: undefined,
        selectedZoomMaximum: undefined,
      },
    },
    realWorldTotalAttributableEquityChart: {
      selectedYear: undefined,
      selectedZoom: {
        selectedZoomMode: undefined,
        selectedZoomMinimum: undefined,
        selectedZoomMaximum: undefined,
      },
    },
    realWorldByInstrumentChart: {
      selectedYear: undefined,
      selectedInstrument: undefined,
      selectedZoom: {
        selectedZoomMode: undefined,
        selectedZoomMinimum: undefined,
        selectedZoomMaximum: undefined,
      },
    },
  },
  capitalStructure: {
    selectedTab: undefined,
    buildStructureOwnerTabs: {},
    buildStructureOwnerTabsErf: {},
    buildStructure: {
      selectedCase: undefined,
    },
    buildStructureExpandedEvents: {},
    expandStructureInstruments: {},
    expandStructureSelectedRadio: {},
    capTableErfSelectedEventIndex: undefined,
    payoutLogic: {
      selectedSubTab: undefined,
      tabsHorizontalScrollPosition: null,
    },
    participationTable: {
      selectedYear: undefined,
    },
    dividends: {
      showHistoricYears: false,
      selectedCase: undefined,
      lastPwermCase: undefined,
    },
  },
  costOfEquity: {
    selectedTab: undefined,
  },
  valuationInputs: {
    selectedTab: undefined,
  },
  overallConclusions: {
    showPerShareFigures: true,
  },
  sampleWaterfallTableExpandStates: {},
  firstLastWaterfallTableExpandStates: {
    valuation: true,
    instruments: {},
  },
  sponsorIrrAndHurdle: {
    selectedTab: undefined,
    irrHurdleCalculations: {
      selectedCase: undefined,
      selectedInstrument: undefined,
    },
    sponsorIrr: {
      selectedCase: undefined,
    },
  },
};

export interface UiValuesState {
  activeTab: DealThesisSidebarTabs;
  activeInstrument?: {
    instrumentId?: string;
    eventId?: string;
  };
  isAuthenticated: boolean;
  highlightEditableCells: boolean;
  discountRateOverrideEnabled: boolean;
  expandAllDiscountRateOverride: boolean;
  expandedDiscountRateOverrideInstruments: Record<string, boolean>;
  showOriginalDiscountRates: boolean;
  hideUnrelatedEvents: boolean;
  hideUnrelatedEventsToggleDisplay: boolean;
  obtrusiveRequestsInProgressCount: number;
  unobtrusiveRequestsInProgressCount: number;
  appInsightsConnectionString?: string;
  datadogApplicationId?: string;
  datadogClientToken?: string;
  datadogEnvironment?: string;
  probabilityWeighted?: {
    maxCaseNameLength: Nullable<number>;
    maxCases: Nullable<number>;
    maxFinancialMetricNameLength: Nullable<number>;
    maxFinancialMetrics: Nullable<number>;
    maxForecastYears: Nullable<number>;
    maxMultiplesPerCase: Nullable<number>;
    maxValuationDateNarrativeLength: Nullable<number>;
  };
  sessionSampleRate?: number;
  sessionReplaySampleRate?: number;
  versionTag?: string;
  branchName?: string;
  commitId?: string;
  validOpmIterationCounts: number[];
  maxRefinancingEventsPerCalc?: number;
  maxRefinancingEventNameLength: number;
  maxYearsBetweenEventsAndValuation: number;
  maxMonthsBetweenValuationAndFYED: number;
  maxCapitalStructureYears: number;
  opmWaterfallIterations?: number;
  opmTableCollapseStates: {
    [key: string]: boolean;
  };
  pwermWaterfallTableExpandStates: {
    [key: string]: boolean;
  };
  sponsorIrrExpandYears: {
    [key: string]: boolean;
  };
  sponsorIrrExpandMultiples: {
    [key: string]: boolean;
  };
  irrHurdleCalculationsExpandStates: {
    [key: string]: boolean;
  };
  expandAll: OverallExpandState;
  expandAllPwermWaterfall: boolean;
  expandAllSponsorIrr: boolean;
  expandAllIrrHurdleCalculations: boolean;
  expandAllSampleWaterfall: boolean;
  expandAllFirstLastWaterfall: boolean;
  userSelections: UserSelections;
  displayOperationProgress: boolean;
  operationProgress?: OperationProgressUpdateDto;
  operationStatus: {
    isSaving: boolean;
    isOpmCalcRunning: boolean;
    isExportRunning: boolean;
    isCaseBeingAdded: boolean;
  };
  showOnlyUserAccessibleProjects: boolean;
  cookieConsentOneTrustID: string | undefined;
  minDate: string;
  pageScrollPositionList: {
    [key: string]: PageScrollPosition[];
  };
  maintenance: MaintenanceModeDto;
  compatibilityLevel: CompatibilityLevelDto;
}

export const fetchApplicationConfiguration = createAsyncThunk<
  { configuration: ApplicationConfigurationDto },
  void,
  { state: RootState; dispatch: AppDispatch }
>('uiValues/fetchApplicationConfiguration', async () => {
  const response = await api.get<ApplicationConfigurationDto>(`${env.apiUrl}/configuration`);
  setServerFeatureFlags(response.data.featureFlags);
  return { configuration: response.data };
});

export const setOperationStepComplete = createAsyncThunk(
  'uiValues/setOperationStepComplete',
  async (payload: { stepType: OperationsStepType; progress: number }, { dispatch, getState }) => {
    const state = getState() as RootState;
    const currentOperationProgress = state.uiValues.operationProgress;
    if (currentOperationProgress) {
      const stepIndex = currentOperationProgress.steps.findIndex(
        (s) => s.type === payload.stepType
      );
      if (stepIndex !== -1) {
        const newSteps = [...currentOperationProgress.steps];
        newSteps[stepIndex] = { ...newSteps[stepIndex], complete: true };
        const newOperationProgress = {
          ...currentOperationProgress,
          steps: newSteps,
          progress: payload.progress,
        };
        dispatch(setOperationProgress(newOperationProgress));
      }
    }
  }
);

export const setDisplayOperationProgress = createAsyncThunk(
  'uiValues/setDisplayOperationProgress',
  async (payload: boolean, { dispatch, getState }) => {
    const state = getState() as RootState;
    const isOpmCalcRunning = state.uiValues.operationStatus.isOpmCalcRunning;
    const isExportRunning = state.uiValues.operationStatus.isExportRunning;
    if (payload === false) {
      await new Promise((resolve) => setTimeout(resolve, LOADER_HIDE_TIMEOUT));
      // TODO: if we allow simultaneous operations, we need to change this logic
      if (isOpmCalcRunning) {
        dispatch(setOpmCalcRunningStatus(false));
      }
      if (isExportRunning) {
        dispatch(setExportRunningStatus(false));
      }
    }
    dispatch(setDisplayOperationProgressSync(payload));
    dispatch(clearOperationProgress());
  }
);

export const resetUserSelections = createAsyncThunk(
  'uiValues/resetUserSelections',
  async (_, { dispatch }) => {
    dispatch(setUserSelections(initialUserSelections));
  }
);

export const uiValuesSlice = createSlice({
  name: 'uiValues',
  initialState: {
    activeTab: DealThesisSidebarTabs.Inputs,
    activeInstrument: {
      instrumentId: undefined,
      eventId: undefined,
    },
    isAuthenticated: false,
    highlightEditableCells: false,
    discountRateOverrideEnabled: false,
    expandAllDiscountRateOverride: false,
    expandedDiscountRateOverrideInstruments: {},
    showOriginalDiscountRates: false,
    hideUnrelatedEvents: true,
    hideUnrelatedEventsToggleDisplay: true,
    obtrusiveRequestsInProgressCount: 0,
    unobtrusiveRequestsInProgressCount: 0,
    appInsightsConnectionString: undefined,
    versionTag: undefined,
    branchName: undefined,
    commitId: undefined,
    validOpmIterationCounts: [],
    maxRefinancingEvents: undefined,
    maxHistoricalEntries: undefined,
    maxRefinancingEventNameLength: 0,
    maxYearsBetweenEventsAndValuation: 0,
    maxMonthsBetweenValuationAndFYED: 0,
    maxCapitalStructureYears: 0,
    waterfallIterations: undefined,
    opmTableCollapseStates: {},
    pwermWaterfallTableExpandStates: {
      [WaterfallSectionsErf.ExitMultiples]: true,
      [WaterfallSectionsErf.EnterpriseValueEV]: true,
      [WaterfallSectionsErf.NetExitProceeds]: true,
      [WaterfallSectionsErf.OrdinaryEquity]: true,
      [WaterfallSectionsErf.InterimCashFlows]: true,
      [WaterfallSectionsErf.ShareholderDebtDistributions]: true,
      [WaterfallSectionsErf.SponsorReturn]: true,
      [WaterfallSectionsErf.TotalEquityDCFCrossCheck]: true,
      [WaterfallSectionsErf.TotalEquityProceeds]: true,
      [WaterfallSectionsErf.Valuation]: true,
    },
    sponsorIrrExpandYears: {},
    sponsorIrrExpandMultiples: {},
    irrHurdleCalculationsExpandStates: {},
    expandAll: OverallExpandState.Other,
    expandAllPwermWaterfall: true,
    expandAllSponsorIrr: true,
    expandAllIrrHurdleCalculations: true,
    sampleWaterfallTableExpandStates: {},
    expandAllSampleWaterfall: true,
    expandAllFirstLastWaterfall: true,
    userSelections: initialUserSelections,
    displayOperationProgress: false,
    operationProgress: undefined,
    operationStatus: {
      isSaving: false,
      isOpmCalcRunning: false,
      isExportRunning: false,
      isCaseBeingAdded: false,
    },
    showOnlyUserAccessibleProjects: true,
    cookieConsentOneTrustID: undefined,
    minDate: '1900-01-01',
    pageScrollPositionList: {},
    maintenance: {
      start: null,
      timeToStartMins: null,
      end: null,
      message: null,
      active: null,
      version: '',
    },
    compatibilityLevel: {
      currentLevel: null,
      levels: [],
    },
  } as UiValuesState,
  reducers: {
    setDisplayOperationProgressSync: (state, { payload }) => {
      state.displayOperationProgress = payload;
    },
    setOperationProgress: (state, { payload }) => {
      state.operationProgress = payload;
    },
    clearOperationProgress: (state) => {
      state.operationProgress = undefined;
    },
    editInputs: (state) => {
      state.activeTab = DealThesisSidebarTabs.Inputs;
    },
    setActiveInstrument: (state, payload) => {
      state.activeInstrument = payload.payload;
    },
    setIsLoggedIn: (state, { payload }) => {
      state.isAuthenticated = payload;
    },
    setHighlightEditableCells: (state, payload) => {
      state.highlightEditableCells = payload.payload;
    },
    setDiscountRateOverrideEnabled: (state, payload) => {
      state.discountRateOverrideEnabled = payload.payload;
    },
    setExpandAllDiscountRateOverride: (state, payload) => {
      state.expandAllDiscountRateOverride = payload.payload;
    },
    setExpandedDiscountRateOverrideInstruments: (state, payload) => {
      state.expandedDiscountRateOverrideInstruments = payload.payload;
    },
    toggleDiscountRateOverrideInstrument: (state, payload) => {
      const instrumentId = payload.payload;
      state.expandedDiscountRateOverrideInstruments[instrumentId] =
        !state.expandedDiscountRateOverrideInstruments[instrumentId];
    },
    setShowOriginalDiscountRates: (state, payload) => {
      state.showOriginalDiscountRates = payload.payload;
    },
    setHideUnrelatedEvents: (state, payload) => {
      state.hideUnrelatedEvents = payload.payload;
    },
    setHideUnrelatedEventsToggleDisplay: (state, payload) => {
      state.hideUnrelatedEventsToggleDisplay = payload.payload;
    },
    increaseObtrusiveRequestsInProgressCount: (state) => {
      state.obtrusiveRequestsInProgressCount = state.obtrusiveRequestsInProgressCount + 1;
    },
    decreaseObtrusiveRequestsInProgressCount: (state) => {
      state.obtrusiveRequestsInProgressCount =
        state.obtrusiveRequestsInProgressCount === 0
          ? 0
          : state.obtrusiveRequestsInProgressCount - 1;
    },
    increaseUnobtrusiveRequestsInProgressCount: (state) => {
      state.unobtrusiveRequestsInProgressCount = state.unobtrusiveRequestsInProgressCount + 1;
    },
    decreaseUnobtrusiveRequestsInProgressCount: (state) => {
      state.unobtrusiveRequestsInProgressCount =
        state.unobtrusiveRequestsInProgressCount === 0
          ? 0
          : state.unobtrusiveRequestsInProgressCount - 1;
    },
    setAppConfigurationData: (state, { payload }) => {
      state.versionTag = payload.versionTag;
      state.commitId = payload.commitId;
      state.branchName = payload.branchName;
      state.validOpmIterationCounts = payload.validOpmIterationCounts;
    },
    setTableCollapseState: (state, payload) => {
      const { key, value } = payload.payload;
      state.opmTableCollapseStates[key] = value;
    },
    clearTableCollapseStates: (state) => {
      state.opmTableCollapseStates = {};
    },
    setExpandAllState: (state, payload) => {
      state.expandAll = payload.payload;
    },
    setUserSelections: (state, payload) => {
      state.userSelections = payload.payload;
    },
    setOpmWaterfallSelectedTab: (state, payload) => {
      state.userSelections.opmWaterfall.selectedTab = payload.payload;
    },
    setOpmWaterfallFirstLastTableSelectedYear: (state, payload) => {
      state.userSelections.opmWaterfall.firstLastTable.selectedYear = payload.payload;
    },
    setOpmWaterfallFirstLastTableSelectedInstrument: (state, payload) => {
      state.userSelections.opmWaterfall.firstLastTable.selectedInstrument = payload.payload;
    },
    setOpmWaterfallSampleWaterfallSelectedYear: (state, payload) => {
      state.userSelections.opmWaterfall.sampleWaterfall.selectedYear = payload.payload;
    },
    setOpmWaterfallSampleWaterfallSelectedInstrument: (state, payload) => {
      state.userSelections.opmWaterfall.sampleWaterfall.selectedInstrument = payload.payload;
    },
    setOpmWaterfallSampleWaterfallSelectedEnterpriseValue: (state, payload) => {
      state.userSelections.opmWaterfall.sampleWaterfall.selectedEnterpriseValue = payload.payload;
    },
    setPwermWaterfallTableCollapseState: (state, payload) => {
      const { key, value } = payload.payload;
      state.pwermWaterfallTableExpandStates[key] = value;
    },
    resetCapitalStructureCapTableSelectedEventIndex: (state) => {
      state.userSelections.capitalStructure.capTableErfSelectedEventIndex = undefined;
    },
    resetCapitalStructureExpandedEvents: (state) => {
      state.userSelections.capitalStructure.buildStructureExpandedEvents = {};
    },
    setExpandStructureInstruments: (
      state,
      payload: { payload: { eventId: string; instruments: Record<string, boolean> } }
    ) => {
      const { eventId, instruments } = payload.payload;
      state.userSelections.capitalStructure.expandStructureInstruments[eventId] = instruments;
    },
    resetExpandStructureInstruments: (state) => {
      state.userSelections.capitalStructure.expandStructureInstruments = {};
      state.userSelections.capitalStructure.expandStructureSelectedRadio = {};
    },
    setExpandStructureSelectedRadio: (
      state,
      payload: { payload: { eventId: string; selectedRadio: string } }
    ) => {
      const { eventId, selectedRadio } = payload.payload;
      state.userSelections.capitalStructure.expandStructureSelectedRadio[eventId] = selectedRadio;
    },
    setExpandAllPwermWaterfallState: (state, payload) => {
      state.expandAllPwermWaterfall = payload.payload;
    },
    setSponsorIrrTableExpandYears: (state, payload: { payload: Record<string, boolean> }) => {
      const expandStates = payload.payload;
      Object.entries(expandStates).forEach(([key, value]) => {
        state.sponsorIrrExpandYears[key] = value;
      });
    },
    setSponsorIrrTableExpandMultiples: (state, payload: { payload: Record<string, boolean> }) => {
      const expandStates = payload.payload;
      Object.entries(expandStates).forEach(([key, value]) => {
        state.sponsorIrrExpandMultiples[key] = value;
      });
    },
    setIrrHurdleCalculationsTableExpandStates: (
      state,
      payload: { payload: Record<string, boolean> }
    ) => {
      const expandStates = payload.payload;
      Object.entries(expandStates).forEach(([key, value]) => {
        state.irrHurdleCalculationsExpandStates[key] = value;
      });
    },
    setExpandAllSponsorIrrState: (state, payload) => {
      state.expandAllSponsorIrr = payload.payload;
    },
    setExpandAllIrrHurdleCalculationsState: (state, payload) => {
      state.expandAllIrrHurdleCalculations = payload.payload;
    },
    setIrrHurdleSelectedCase: (state, payload) => {
      state.userSelections.sponsorIrrAndHurdle.irrHurdleCalculations.selectedCase = payload.payload;
    },
    setIrrHurdleSelectedInstrument: (state, payload) => {
      state.userSelections.sponsorIrrAndHurdle.irrHurdleCalculations.selectedInstrument =
        payload.payload;
    },
    setAllPwermWaterfallTableCollapseState: (state, payload) => {
      const keys = Object.keys(state.pwermWaterfallTableExpandStates);
      keys.forEach((key) => {
        state.pwermWaterfallTableExpandStates[key] = payload.payload;
      });
    },
    setSampleWaterfallTableExpandState: (state, payload) => {
      state.userSelections.sampleWaterfallTableExpandStates = payload.payload;
    },
    setFirstLastWaterfallTableExpandState: (state, payload) => {
      state.userSelections.firstLastWaterfallTableExpandStates = payload.payload;
    },
    setExpandAllSampleWaterfallState: (state, payload) => {
      state.expandAllSampleWaterfall = payload.payload;
    },
    setExpandAllFirstLastWaterfallState: (state, payload) => {
      state.expandAllFirstLastWaterfall = payload.payload;
    },
    setAllSampleWaterfallTableExpandState: (state, payload) => {
      const setAllBooleans = (obj: NestedBooleanObject, value: boolean) => {
        Object.keys(obj).forEach((key) => {
          if (typeof obj[key] === 'boolean') {
            obj[key] = value;
          } else if (typeof obj[key] === 'object' && obj[key] !== null) {
            setAllBooleans(obj[key] as NestedBooleanObject, value);
          }
        });
      };
      setAllBooleans(state.userSelections.sampleWaterfallTableExpandStates, payload.payload);
    },
    setAllFirstLastWaterfallExpandState: (state, payload) => {
      const setAllBooleans = (obj: NestedBooleanObject, value: boolean) => {
        Object.keys(obj).forEach((key) => {
          if (typeof obj[key] === 'boolean') {
            obj[key] = value;
          } else if (typeof obj[key] === 'object' && obj[key] !== null) {
            setAllBooleans(obj[key] as NestedBooleanObject, value);
          }
        });
      };
      setAllBooleans(state.userSelections.firstLastWaterfallTableExpandStates, payload.payload);
    },
    setDealThesisSelectedTab: (state, payload) => {
      state.userSelections.dealThesis.selectedTab = payload.payload;
    },
    setOpmShowHistoricYears: (state, payload) => {
      state.userSelections.opmInputs.showHistoricDataColumns = payload.payload;
    },
    setDealThesisShowHistoricYears: (state, payload) => {
      state.userSelections.dealThesis.showHistoricDataColumns = payload.payload;
    },
    setPwermDashboardValuationEstimatesTableSelectedInstrument: (state, payload) => {
      state.userSelections.pwermDashboard.valuationEstimatesTable.selectedInstrument =
        payload.payload;
    },
    setPwermDashboardEvolutionChartSelectedCase: (state, payload) => {
      state.userSelections.pwermDashboard.evEvolutionChart.selectedCase = payload.payload;
    },
    setPwermDashboardEvolutionChartIsExpanded: (state, payload) => {
      state.userSelections.pwermDashboard.evEvolutionChart.isExpanded = payload.payload;
    },
    setPwermDashboardTotalEquityChartSelectedCase: (state, payload) => {
      state.userSelections.pwermDashboard.totalEquityChart.selectedCase = payload.payload;
    },
    setPwermDashboardTotalEquityChartIsExpanded: (state, payload) => {
      state.userSelections.pwermDashboard.totalEquityChart.isExpanded = payload.payload;
    },
    setPwermDashboardKeyMetricsAndCrossChecksTableIsExpanded: (state, payload) => {
      state.userSelections.pwermDashboard.keyMetricsAndCrossChecksTable.isExpanded =
        payload.payload;
    },
    setPwermDashboard2ValuationEstimatesTableSelectedInstrument: (state, payload) => {
      state.userSelections.pwerm2Dashboard.valuationEstimatesTable.selectedInstrument =
        payload.payload;
    },
    setPwermDashboard2EvolutionChartSelectedCase: (state, payload) => {
      state.userSelections.pwerm2Dashboard.evEvolutionChart.selectedCase = payload.payload;
    },
    setPwermDashboard2EvolutionChartIsExpanded: (state, payload) => {
      state.userSelections.pwerm2Dashboard.evEvolutionChart.isExpanded = payload.payload;
    },
    setPwermDashboard2TotalEquityChartSelectedCase: (state, payload) => {
      state.userSelections.pwerm2Dashboard.totalEquityChart.selectedCase = payload.payload;
    },
    setPwermDashboard2TotalEquityChartIsExpanded: (state, payload) => {
      state.userSelections.pwerm2Dashboard.totalEquityChart.isExpanded = payload.payload;
    },
    setPwermDashboard2KeyMetricsAndCrossChecksTableIsExpanded: (state, payload) => {
      state.userSelections.pwerm2Dashboard.keyMetricsAndCrossChecksTable.isExpanded =
        payload.payload;
    },
    setOpmDashboardRiskNeutralChartSelectedYear: (state, payload) => {
      state.userSelections.opmDashboard.riskNeutralChart.selectedYear = payload.payload;
    },
    setOpmDashboardRiskNeutralChartSelectedInstrument: (state, payload) => {
      state.userSelections.opmDashboard.riskNeutralChart.selectedInstrument = payload.payload;
    },
    setOpmDashboardRiskNeutralChartIsExpanded: (state, payload) => {
      state.userSelections.opmDashboard.riskNeutralChart.isExpanded = payload.payload;
    },
    setOpmDashboardValuationEstimatesTableChildRowStates: (state, payload) => {
      state.userSelections.opmDashboard.valuationEstimatesTable.childRowStates = payload.payload;
    },
    setOpmDashboardInputSummaryTableIsEventsExpanded: (state, payload) => {
      state.userSelections.opmDashboard.inputSummaryTable.isEventsExpanded = payload.payload;
    },
    setOpmDashboardInputSummaryTableIsDlocInstrumentsExpanded: (state, payload) => {
      state.userSelections.opmDashboard.inputSummaryTable.isDlocInstrumentsExpanded =
        payload.payload;
    },
    setOpmDashboardInputSummaryTableIsDlomInstrumentsExpanded: (state, payload) => {
      state.userSelections.opmDashboard.inputSummaryTable.isDlomInstrumentsExpanded =
        payload.payload;
    },
    setRealWorldDistributionsRealWorldChartSelectedYear: (state, payload) => {
      state.userSelections.realWorldDistributions.realWorldChart.selectedYear = payload.payload;
    },
    setRealWorldDistributionsRealWorldChartSelectedInstrument: (state, payload) => {
      state.userSelections.realWorldDistributions.realWorldChart.selectedInstrument =
        payload.payload;
    },
    setRealWorldDistributionsRealWorldEvChartSelectedYear: (state, payload) => {
      state.userSelections.realWorldDistributions.realWorldEvChart.selectedYear = payload.payload;
    },
    setRealWorldDistributionsRealWorldTotalAttributableEquityChartSelectedYear: (
      state,
      payload
    ) => {
      state.userSelections.realWorldDistributions.realWorldTotalAttributableEquityChart.selectedYear =
        payload.payload;
    },
    setRealWorldDistributionsRealWorldByInstrumentChartSelectedYear: (state, payload) => {
      state.userSelections.realWorldDistributions.realWorldByInstrumentChart.selectedYear =
        payload.payload;
    },
    setRealWorldDistributionsRealWorldByInstrumentChartSelectedInstrument: (state, payload) => {
      state.userSelections.realWorldDistributions.realWorldByInstrumentChart.selectedInstrument =
        payload.payload;
    },
    setCostOfEquitySelectedTab: (state, payload) => {
      state.userSelections.costOfEquity.selectedTab = payload.payload;
    },
    setCapitalStructureSelectedTab: (state, payload) => {
      state.userSelections.capitalStructure.selectedTab = payload.payload;
    },
    setCapitalStructurePayoutLogicSelectedSubTab: (state, payload) => {
      state.userSelections.capitalStructure.payoutLogic.selectedSubTab = payload.payload;
    },
    setCapitalStructurePayoutLogicTabsHorizontalScrollPosition: (state, payload) => {
      state.userSelections.capitalStructure.payoutLogic.tabsHorizontalScrollPosition =
        payload.payload;
    },
    setCapitalStructureDividendsShowHistoricYears: (state, payload) => {
      state.userSelections.capitalStructure.dividends.showHistoricYears = payload.payload;
    },
    setCapitalStructureDividendsSelectedCase: (state, payload) => {
      state.userSelections.capitalStructure.dividends.selectedCase = payload.payload;
    },
    setCapitalStructureDividendsLastPwermCase: (state, payload) => {
      state.userSelections.capitalStructure.dividends.lastPwermCase = payload.payload;
    },
    setCapitalStructureBuildStructureSelectedCase: (state, payload) => {
      state.userSelections.capitalStructure.buildStructure.selectedCase = payload.payload;
    },
    setCapitalStructureBuildStructure: (state, payload) => {
      const { key, value } = payload.payload;
      state.userSelections.capitalStructure.buildStructureOwnerTabs[key] = value;
    },
    setCapitalStructureBuildStructureErf: (state, payload) => {
      const { key, value } = payload.payload;
      state.userSelections.capitalStructure.buildStructureOwnerTabsErf[key] = value;
    },
    setCapitalStructureBuildStructureExpandedEvent: (state, payload) => {
      const { eventId, isExpanded } = payload.payload;
      state.userSelections.capitalStructure.buildStructureExpandedEvents[eventId] = isExpanded;
    },
    setCapTableErfSelectedEventIndex: (state, payload) => {
      state.userSelections.capitalStructure.capTableErfSelectedEventIndex = payload.payload;
    },
    setCapitalStructureParticipationTableSelectedYear: (state, payload) => {
      state.userSelections.capitalStructure.participationTable.selectedYear = payload.payload;
    },
    setValuationInputsSelectedTab: (state, payload) => {
      state.userSelections.valuationInputs.selectedTab = payload.payload;
    },
    setPageScrollPosition: (state, payload) => {
      const { key, value } = payload.payload;
      if (!state.pageScrollPositionList[key]) {
        state.pageScrollPositionList[key] = [];
      }
      const index = state.pageScrollPositionList[key].findIndex(
        (pageScrollPosition) => pageScrollPosition.pageName === value.pageName
      );
      if (index !== -1) {
        state.pageScrollPositionList[key][index] = value;
      } else {
        state.pageScrollPositionList[key].push(value);
      }
    },
    setIsSaving: (state, payload) => {
      state.operationStatus.isSaving = payload.payload;
    },
    setOpmCalcRunningStatus: (state, { payload }) => {
      state.operationStatus.isOpmCalcRunning = payload;
    },
    setExportRunningStatus: (state, { payload }) => {
      state.operationStatus.isExportRunning = payload;
    },
    setIsCaseBeingAddedStatus: (state, { payload }) => {
      state.operationStatus.isCaseBeingAdded = payload;
    },
    setSystemSettings: (state, { payload }) => {
      state.maintenance = {
        start: payload.maintenance.start,
        end: payload.maintenance.end,
        message: payload.maintenance.message,
        active: payload.maintenance.active,
        version: payload.maintenance.version,
      };
    },
    setOpmDashboardRiskNeutralChartSelectedZoom: (state, payload) => {
      state.userSelections.opmDashboard.riskNeutralChart.selectedZoom = payload.payload;
    },
    setRealWorldDistributionsRealWorldChartSelectedZoom: (state, payload) => {
      state.userSelections.realWorldDistributions.realWorldChart.selectedZoom = payload.payload;
    },
    setRealWorldDistributionsRealWorldEvChartSelectedZoom: (state, payload) => {
      state.userSelections.realWorldDistributions.realWorldEvChart.selectedZoom = payload.payload;
    },
    setRealWorldDistributionsRealWorldTotalAttributableEquityChartSelectedZoom: (
      state,
      payload
    ) => {
      state.userSelections.realWorldDistributions.realWorldTotalAttributableEquityChart.selectedZoom =
        payload.payload;
    },
    setRealWorldDistributionsRealWorldByInstrumentChartSelectedZoom: (state, payload) => {
      state.userSelections.realWorldDistributions.realWorldByInstrumentChart.selectedZoom =
        payload.payload;
    },
    setOverallConclusionsShowPerShareFigures: (state, payload) => {
      state.userSelections.overallConclusions.showPerShareFigures = payload.payload;
    },
    setSponsorIrrAndHurdleSelectedTab: (state, payload) => {
      state.userSelections.sponsorIrrAndHurdle.selectedTab = payload.payload;
    },
    setSponsorIrrSelectedCase: (state, payload) => {
      state.userSelections.sponsorIrrAndHurdle.sponsorIrr.selectedCase = payload.payload;
    },
    setIrrHurdleCalculationsSelectedCase: (state, payload) => {
      state.userSelections.sponsorIrrAndHurdle.irrHurdleCalculations.selectedCase = payload.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchApplicationConfiguration.fulfilled, (state, action) => {
      state.appInsightsConnectionString =
        action.payload.configuration.applicationInsights.connectionString ?? undefined;
      state.datadogApplicationId =
        action.payload.configuration.datadogConfiguration.applicationId ?? undefined;
      state.datadogClientToken =
        action.payload.configuration.datadogConfiguration.clientToken ?? undefined;
      state.sessionReplaySampleRate =
        action.payload.configuration.datadogConfiguration.sessionReplaySampleRate ?? 0;
      state.sessionSampleRate =
        action.payload.configuration.datadogConfiguration.sessionSampleRate ?? 0;
      state.datadogEnvironment =
        action.payload.configuration.datadogConfiguration.environment ?? '';
      state.probabilityWeighted = action.payload.configuration.probabilityWeighted;
      state.versionTag = action.payload.configuration.versionInfo?.versionTag ?? undefined;
      state.commitId = action.payload.configuration.versionInfo?.commitId ?? undefined;
      state.branchName = action.payload.configuration.versionInfo?.branchName ?? undefined;
      state.validOpmIterationCounts =
        action.payload.configuration.optionPricing.validIterationCounts;
      state.maxRefinancingEventsPerCalc =
        action.payload.configuration.capitalStructure.maxRefinancingEventsPerCalc;
      state.maxRefinancingEventNameLength =
        action.payload.configuration.capitalStructure.maxRefinancingEventNameLength;
      state.maxYearsBetweenEventsAndValuation =
        action.payload.configuration.capitalStructure.maxYearsBetweenEventsAndValuation;
      state.maxMonthsBetweenValuationAndFYED =
        action.payload.configuration.capitalStructure.maxMonthsBetweenValuationAndFYED;
      state.maxCapitalStructureYears =
        action.payload.configuration.capitalStructure.maxCapitalStructureYears;
      state.opmWaterfallIterations = action.payload.configuration.optionPricing.waterfallIterations;
      state.showOnlyUserAccessibleProjects =
        action.payload.configuration.global.showOnlyUserAccessibleProjects;
      state.cookieConsentOneTrustID = action.payload.configuration.global.cookieConsentOneTrustID;
      state.minDate = action.payload.configuration.global.minDate;
      state.maintenance = {
        start: action.payload.configuration.systemSettings.maintenance.start,
        end: action.payload.configuration.systemSettings.maintenance.end,
        message: action.payload.configuration.systemSettings.maintenance.message,
        active: action.payload.configuration.systemSettings.maintenance.active,
        version: action.payload.configuration.systemSettings.maintenance.version,
      };
      state.compatibilityLevel = action.payload.configuration.projectCompatibilityLevel;
    });
  },
});

export const {
  setDisplayOperationProgressSync,
  setOperationProgress,
  clearOperationProgress,
  editInputs,
  setActiveInstrument,
  setHighlightEditableCells,
  setDiscountRateOverrideEnabled,
  setExpandAllDiscountRateOverride,
  setExpandedDiscountRateOverrideInstruments,
  toggleDiscountRateOverrideInstrument,
  setShowOriginalDiscountRates,
  setOverallConclusionsShowPerShareFigures,
  setSponsorIrrAndHurdleSelectedTab,
  setHideUnrelatedEvents,
  setHideUnrelatedEventsToggleDisplay,
  increaseObtrusiveRequestsInProgressCount,
  decreaseObtrusiveRequestsInProgressCount,
  increaseUnobtrusiveRequestsInProgressCount,
  decreaseUnobtrusiveRequestsInProgressCount,
  setAppConfigurationData,
  setTableCollapseState,
  setExpandAllState,
  clearTableCollapseStates,
  setUserSelections,
  setOpmWaterfallSelectedTab,
  setOpmWaterfallFirstLastTableSelectedYear,
  setOpmWaterfallFirstLastTableSelectedInstrument,
  setOpmWaterfallSampleWaterfallSelectedYear,
  setOpmWaterfallSampleWaterfallSelectedInstrument,
  setOpmWaterfallSampleWaterfallSelectedEnterpriseValue,
  setPwermWaterfallTableCollapseState,
  resetCapitalStructureExpandedEvents,
  resetExpandStructureInstruments,
  setExpandStructureInstruments,
  setExpandStructureSelectedRadio,
  resetCapitalStructureCapTableSelectedEventIndex,
  setAllPwermWaterfallTableCollapseState,
  setExpandAllPwermWaterfallState,
  setSponsorIrrTableExpandYears,
  setSponsorIrrTableExpandMultiples,
  setSponsorIrrSelectedCase,
  setIrrHurdleCalculationsSelectedCase,
  setIrrHurdleCalculationsTableExpandStates,
  setExpandAllSponsorIrrState,
  setExpandAllIrrHurdleCalculationsState,
  setIrrHurdleSelectedCase,
  setIrrHurdleSelectedInstrument,
  setAllSampleWaterfallTableExpandState,
  setAllFirstLastWaterfallExpandState,
  setSampleWaterfallTableExpandState,
  setFirstLastWaterfallTableExpandState,
  setExpandAllSampleWaterfallState,
  setExpandAllFirstLastWaterfallState,
  setDealThesisSelectedTab,
  setOpmShowHistoricYears,
  setDealThesisShowHistoricYears,
  setPwermDashboardValuationEstimatesTableSelectedInstrument,
  setPwermDashboardEvolutionChartSelectedCase,
  setPwermDashboardEvolutionChartIsExpanded,
  setPwermDashboardTotalEquityChartSelectedCase,
  setPwermDashboardTotalEquityChartIsExpanded,
  setPwermDashboardKeyMetricsAndCrossChecksTableIsExpanded,
  setPwermDashboard2ValuationEstimatesTableSelectedInstrument,
  setPwermDashboard2EvolutionChartSelectedCase,
  setPwermDashboard2EvolutionChartIsExpanded,
  setPwermDashboard2TotalEquityChartSelectedCase,
  setPwermDashboard2TotalEquityChartIsExpanded,
  setPwermDashboard2KeyMetricsAndCrossChecksTableIsExpanded,
  setOpmDashboardRiskNeutralChartSelectedYear,
  setOpmDashboardRiskNeutralChartSelectedInstrument,
  setOpmDashboardRiskNeutralChartIsExpanded,
  setOpmDashboardRiskNeutralChartSelectedZoom,
  setOpmDashboardValuationEstimatesTableChildRowStates,
  setOpmDashboardInputSummaryTableIsEventsExpanded,
  setOpmDashboardInputSummaryTableIsDlocInstrumentsExpanded,
  setOpmDashboardInputSummaryTableIsDlomInstrumentsExpanded,
  setRealWorldDistributionsRealWorldChartSelectedYear,
  setRealWorldDistributionsRealWorldChartSelectedInstrument,
  setRealWorldDistributionsRealWorldChartSelectedZoom,
  setRealWorldDistributionsRealWorldEvChartSelectedYear,
  setRealWorldDistributionsRealWorldEvChartSelectedZoom,
  setRealWorldDistributionsRealWorldTotalAttributableEquityChartSelectedYear,
  setRealWorldDistributionsRealWorldTotalAttributableEquityChartSelectedZoom,
  setRealWorldDistributionsRealWorldByInstrumentChartSelectedYear,
  setRealWorldDistributionsRealWorldByInstrumentChartSelectedInstrument,
  setRealWorldDistributionsRealWorldByInstrumentChartSelectedZoom,
  setCostOfEquitySelectedTab,
  setCapitalStructureSelectedTab,
  setCapitalStructurePayoutLogicSelectedSubTab,
  setCapitalStructurePayoutLogicTabsHorizontalScrollPosition,
  setCapitalStructureDividendsShowHistoricYears,
  setCapitalStructureDividendsSelectedCase,
  setCapitalStructureDividendsLastPwermCase,
  setCapitalStructureBuildStructureSelectedCase,
  setCapitalStructureBuildStructure,
  setCapitalStructureBuildStructureErf,
  setCapitalStructureBuildStructureExpandedEvent,
  setCapTableErfSelectedEventIndex,
  setCapitalStructureParticipationTableSelectedYear,
  setValuationInputsSelectedTab,
  setIsSaving,
  setOpmCalcRunningStatus,
  setExportRunningStatus,
  setIsCaseBeingAddedStatus,
  setPageScrollPosition,
  setSystemSettings,
} = uiValuesSlice.actions;

export default uiValuesSlice.reducer;
